import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Download, Share, Plus, Smartphone, Monitor, X } from "lucide-react";
import { LOGO_ONBG_URL } from "../lib/brand";

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const isAndroid = () => /android/i.test(window.navigator.userAgent);
const isMobile = () => isIOS() || isAndroid();
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const DISMISS_KEY = "mpl_install_dismissed_at";
const DISMISS_DAYS = 7;

export function InstallButton({ variant = "shell" }) {
  const [canInstall, setCanInstall] = useState(!!window.__deferredInstallPrompt);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
    };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  // Already running as an installed app, or nothing to offer on this browser.
  if (installed) return null;
  if (!canInstall && !isIOS()) return null;

  const doInstall = async () => {
    const dp = window.__deferredInstallPrompt;
    if (dp) {
      dp.prompt();
      await dp.userChoice;
      window.__deferredInstallPrompt = null;
      setCanInstall(false);
    } else {
      setShowHelp(true);
    }
  };

  const label = "Install app";

  return (
    <>
      {variant === "auth" ? (
        <button
          type="button"
          onClick={doInstall}
          data-testid="install-app-button"
          className="mt-6 mx-auto flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
        >
          <Smartphone className="h-4 w-4 text-[#ff3b30]" /> {label} on your phone
        </button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={doInstall}
          data-testid="install-app-button"
          className="h-9 border-[#ff3b30]/40 bg-[#ff3b30]/10 text-white hover:bg-[#ff3b30]/20 rounded-lg"
        >
          <Download className="h-4 w-4 mr-1.5 text-[#ff3b30]" /> {label}
        </Button>
      )}

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="bg-[#141414] border-white/10 text-white" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              Add to your Home Screen
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Get the app icon on your phone — no App Store needed. It opens
              full-screen just like a native app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {isIOS() ? (
              <ol className="space-y-3 text-sm text-white/80">
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  Tap the <Share className="h-4 w-4 inline mx-1 text-[#ff3b30]" /> <b>Share</b> button in Safari's toolbar.
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  Scroll down and tap <Plus className="h-4 w-4 inline mx-1 text-[#ff3b30]" /> <b>Add to Home Screen</b>.
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  Tap <b>Add</b> — My Pocket Lender is now on your home screen. 🎉
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-white/80">
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  Open your browser menu (⋮).
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  Tap <b>Install app</b> or <b>Add to Home screen</b>.
                </li>
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


function IOSSteps() {
  return (
    <ol className="space-y-3 text-sm text-white/80">
      <li className="flex items-center gap-3">
        <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">1</span>
        Tap the <Share className="h-4 w-4 inline mx-1 text-[#ff3b30]" /> <b>Share</b> button in Safari's toolbar.
      </li>
      <li className="flex items-center gap-3">
        <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">2</span>
        Scroll down and tap <Plus className="h-4 w-4 inline mx-1 text-[#ff3b30]" /> <b>Add to Home Screen</b>.
      </li>
      <li className="flex items-center gap-3">
        <span className="h-6 w-6 rounded-full bg-[#ff3b30] text-white flex items-center justify-center text-xs font-bold shrink-0">3</span>
        Tap <b>Add</b> — My Pocket Lender is now on your home screen. 🎉
      </li>
    </ol>
  );
}

export function InstallBanner() {
  const location = useLocation();
  const publicPage = ["/", "/login", "/signup"].includes(location.pathname);
  const [deferred, setDeferred] = useState(window.__deferredInstallPrompt || null);
  const [visible, setVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (publicPage) { setVisible(false); return; }
    if (isStandalone()) return;
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (last && Date.now() - last < DISMISS_DAYS * 86400000) return;

    const onInstallable = () => {
      setDeferred(window.__deferredInstallPrompt);
      setVisible(true);
    };
    const onInstalled = () => setVisible(false);
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);

    const t = setTimeout(() => {
      if (window.__deferredInstallPrompt) {
        setDeferred(window.__deferredInstallPrompt);
        setVisible(true);
      } else if (isIOS()) {
        setVisible(true);
      }
    }, 1600);

    return () => {
      clearTimeout(t);
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, [publicPage]);

  if (publicPage || !visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      window.__deferredInstallPrompt = null;
      dismiss();
    } else {
      setShowHelp(true);
    }
  };

  const desktop = !isMobile();
  const title = desktop ? "Install the desktop app" : "Get the app on your phone";
  const msg = desktop
    ? "Save My Pocket Lender to your Start menu for one-click access — no browser tab needed."
    : isIOS()
      ? "Add My Pocket Lender to your Home Screen for full-screen, app-like access."
      : "Install My Pocket Lender on your phone — no App Store needed.";

  return (
    <>
      <div
        data-testid="install-banner"
        className="fixed z-40 left-1/2 -translate-x-1/2 bottom-4 w-[calc(100%-1.5rem)] max-w-md
                   sm:left-auto sm:right-5 sm:translate-x-0
                   bg-[#141414]/95 backdrop-blur-xl border border-white/12 rounded-2xl shadow-2xl shadow-black/60
                   p-4 flex items-center gap-3 animate-in slide-in-from-bottom-4 fade-in duration-300"
      >
        <img src={LOGO_ONBG_URL} alt="" className="h-12 w-12 rounded-xl shrink-0 shadow-lg shadow-[#ff3b30]/20" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-base tracking-tight flex items-center gap-1.5 text-white">
            {desktop ? <Monitor className="h-4 w-4 text-[#ff3b30]" /> : <Smartphone className="h-4 w-4 text-[#ff3b30]" />}
            {title}
          </p>
          <p className="text-xs text-white/55 mt-0.5 leading-snug">{msg}</p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm"
            onClick={install}
            data-testid="install-banner-install"
            className="h-8 bg-[#ff3b30] hover:bg-[#ff5147] text-white rounded-lg text-xs font-semibold px-3"
          >
            <Download className="h-3.5 w-3.5 mr-1" /> Install
          </Button>
          <button
            onClick={dismiss}
            data-testid="install-banner-dismiss"
            className="text-[11px] text-white/45 hover:text-white/70 transition-colors flex items-center justify-center gap-0.5"
          >
            <X className="h-3 w-3" /> Later
          </button>
        </div>
      </div>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="bg-[#141414] border-white/10 text-white" data-testid="install-banner-help">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Add to your Home Screen</DialogTitle>
            <DialogDescription className="text-white/60">
              Get the app icon on your phone — no App Store needed. Opens full-screen like a native app.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <IOSSteps />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
